/**
 * FileUpload.tsx
 *
 * 说明：
 * - 提供一个与项目样式一致的文件选择与预览组件，用于表单中接收文件或图片
 * - 组件本身不负责对接模型/后端调用（上传、base64 转换由上层表单逻辑处理）
 * - 返回的文件项类型遵循项目约定 UploadFileItem：{ id?: string; file?: File; src?: string }
 * - 支持多图上传、排序（左移/右移），保证上层接收到的数组顺序与用户在界面上看到的排序一致
 * - 通过必填的 maxFiles 参数统一控制图片数量上限，避免散落在组件外部的硬编码
 * - 所有注释使用中文，保持与项目注释规范一致
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// 使用 shadcn 组件替换 MUI
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';
import {
  ImagePlus,
  ArrowLeft,
  ArrowRight,
  Eye,
  Trash2,
  SlidersHorizontal,
  Loader2,
} from 'lucide-react';
import {
  DEFAULT_MAX_IMAGE_DIMENSION,
  loadImageElement,
  readImageDimensions,
  normalizeImageFile,
  type ImageDimensions,
} from './file-upload/imageConstraints';
import type { ImageResizeMode, ImageValidationConstraints } from '@/shared/media/imageValidationTypes';
import { inspectPngTransparency } from './file-upload/imageValidation';

export type UploadFileItem = {
  id?: string;
  file?: File;
  src?: string;
  dimensions?: { width: number; height: number };
};

type ProcessedFileItem = {
  id: string;
  file: File;
  src: string;
  dimensions?: ImageDimensions;
};

export interface FileUploadProps {
  value?: UploadFileItem[];
  onFilesChange: (files: UploadFileItem[]) => void;
  accept?: string;
  multiple?: boolean;
  maxSizeMB?: number;
  maxDimension?: number;
  /** 图片缩放策略：fit=自动缩放到 maxDimension；none=不自动缩放 */
  imageResizeMode?: ImageResizeMode;
  /** 图片校验约束（仅在上传图片时生效） */
  imageConstraints?: ImageValidationConstraints;
  /** 是否显示默认提示文案（数量/尺寸提示等） */
  showHints?: boolean;
  maxFiles: number;
  onLimitExceeded?: (maxFiles: number) => void;
  autoUpload?: boolean; // 保留字段，由上层决定是否自动上传
  uploadMockLatency?: number; // 保留字段，用于测试延迟（本组件不做真实上传）
}

type FilePreviewTitleResolver = (item: UploadFileItem) => string;

type ValidationMessage = { type: 'info' | 'error'; text: string };

const isVideoLike = (item?: UploadFileItem | null): boolean => {
  if (!item) return false;
  const rawType = item.file?.type ?? '';
  if (rawType.startsWith('video/')) {
    return true;
  }
  const src = (item.src ?? '').trim();
  if (!src) return false;
  return src.startsWith('data:video') || /\.(mp4|mov|webm|m4v)(\?.*)?$/i.test(src);
};

export const useFilePreview = (options?: { getTitle?: FilePreviewTitleResolver }) => {
  const [previewItem, setPreviewItem] = useState<UploadFileItem | null>(null);
  const titleResolver = options?.getTitle;

  const previewTitle = useMemo(() => {
    if (!previewItem) {
      return '';
    }
    if (titleResolver) {
      return titleResolver(previewItem);
    }
    return previewItem.file?.name ?? previewItem.id ?? '预览';
  }, [previewItem, titleResolver]);

  const previewSrc = useMemo(() => {
    if (!previewItem) {
      return '';
    }
    return (previewItem.src ?? '').trim();
  }, [previewItem]);

  const isVideoPreview = useMemo(() => isVideoLike(previewItem), [previewItem]);

  const openPreview = useCallback((item: UploadFileItem) => {
    setPreviewItem(item);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewItem(null);
  }, []);

  return {
    previewItem,
    previewTitle,
    previewSrc,
    isVideoPreview,
    openPreview,
    closePreview,
  };
};

export interface FilePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  src: string;
  isVideo?: boolean;
  emptyText?: string;
  closeText?: string;
}

export const FilePreviewDialog: React.FC<FilePreviewDialogProps> = ({
  open,
  onOpenChange,
  title,
  src,
  isVideo = false,
  emptyText = '暂无可预览内容',
  closeText = '关闭预览',
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">
            {isVideo ? '视频预览' : '图片预览'}
          </DialogDescription>
        </DialogHeader>
        <div className={isVideo ? 'flex items-center justify-center bg-black' : 'flex items-center justify-center bg-neutral-900'}>
          {src ? (
            isVideo ? (
              <video src={src} controls autoPlay className="max-h-[75vh] max-w-full" />
            ) : (
              <img src={src} alt={title || 'preview'} className="max-h-[75vh] max-w-full" />
            )
          ) : (
            <p className="text-sm text-muted-foreground">{emptyText}</p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {closeText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const releaseObjectUrl = (item?: UploadFileItem) => {
  const src = item?.src;
  if (src && src.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(src);
    } catch {
      // ignore
    }
  }
};

const releaseRange = (list: UploadFileItem[], start: number) => {
  for (let i = start; i < list.length; i += 1) {
    releaseObjectUrl(list[i]);
  }
};

const releaseAll = (list: UploadFileItem[]) => {
  for (const it of list) {
    releaseObjectUrl(it);
  }
};

// MUI 样式替换为 tailwind className，故移除 sx 常量
const tooltipWrapperStyle: React.CSSProperties = { display: 'inline-flex' };

const gaussianKernel5x5 = new Float32Array([
  2, 4, 5, 4, 2,
  4, 9, 12, 9, 4,
  5, 12, 15, 12, 5,
  4, 9, 12, 9, 4,
  2, 4, 5, 4, 2,
]);

const gaussianDivisor = 159;

const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

const applyCannyEdgeDetection = async (src: string, lowThreshold: number, highThreshold: number): Promise<string> => {
  const image = await loadImageElement(src);
  const width = Math.max(image.naturalWidth || image.width, 1);
  const height = Math.max(image.naturalHeight || image.height, 1);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('CANVAS_CONTEXT_UNAVAILABLE');
  }
  ctx.drawImage(image, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  const { data } = imageData;
  const grayscale = new Float32Array(width * height);
  for (let i = 0; i < width * height; i += 1) {
    const idx = i * 4;
    const r = data[idx];
    const g = data[idx + 1];
    const b = data[idx + 2];
    grayscale[i] = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  const blurred = new Float32Array(width * height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let sum = 0;
      for (let ky = -2; ky <= 2; ky += 1) {
        for (let kx = -2; kx <= 2; kx += 1) {
          const sampleX = clamp(x + kx, 0, width - 1);
          const sampleY = clamp(y + ky, 0, height - 1);
          const weight = gaussianKernel5x5[(ky + 2) * 5 + (kx + 2)];
          sum += grayscale[sampleY * width + sampleX] * weight;
        }
      }
      blurred[y * width + x] = sum / gaussianDivisor;
    }
  }

  const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
  const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];
  const gradient = new Float32Array(width * height);
  const direction = new Float32Array(width * height);

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      let gx = 0;
      let gy = 0;
      for (let ky = -1; ky <= 1; ky += 1) {
        for (let kx = -1; kx <= 1; kx += 1) {
          const sample = blurred[(y + ky) * width + (x + kx)];
          const kernelIdx = (ky + 1) * 3 + (kx + 1);
          gx += sample * sobelX[kernelIdx];
          gy += sample * sobelY[kernelIdx];
        }
      }
      const magnitude = Math.hypot(gx, gy);
      const idx = y * width + x;
      gradient[idx] = magnitude;
      let angle = (Math.atan2(gy, gx) * 180) / Math.PI;
      if (angle < 0) {
        angle += 180;
      }
      direction[idx] = angle;
    }
  }

  const suppressed = new Float32Array(width * height);
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const idx = y * width + x;
      const angle = direction[idx];
      const magnitude = gradient[idx];
      let neighbor1 = 0;
      let neighbor2 = 0;

      if ((angle >= 0 && angle < 22.5) || angle >= 157.5) {
        neighbor1 = gradient[idx + 1];
        neighbor2 = gradient[idx - 1];
      } else if (angle >= 22.5 && angle < 67.5) {
        neighbor1 = gradient[idx + width + 1];
        neighbor2 = gradient[idx - width - 1];
      } else if (angle >= 67.5 && angle < 112.5) {
        neighbor1 = gradient[idx + width];
        neighbor2 = gradient[idx - width];
      } else {
        neighbor1 = gradient[idx + width - 1];
        neighbor2 = gradient[idx - width + 1];
      }

      if (magnitude >= neighbor1 && magnitude >= neighbor2) {
        suppressed[idx] = magnitude;
      } else {
        suppressed[idx] = 0;
      }
    }
  }

  const strong = 255;
  const weak = 75;
  const result = new Uint8ClampedArray(width * height);
  for (let i = 0; i < width * height; i += 1) {
    const value = suppressed[i];
    if (value >= highThreshold) {
      result[i] = strong;
    } else if (value >= lowThreshold) {
      result[i] = weak;
    } else {
      result[i] = 0;
    }
  }

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const idx = y * width + x;
      if (result[idx] !== weak) {
        continue;
      }
      let hasStrongNeighbor = false;
      for (let ky = -1; ky <= 1 && !hasStrongNeighbor; ky += 1) {
        for (let kx = -1; kx <= 1; kx += 1) {
          const neighborIdx = (y + ky) * width + (x + kx);
          if (result[neighborIdx] === strong) {
            hasStrongNeighbor = true;
            break;
          }
        }
      }
      result[idx] = hasStrongNeighbor ? strong : 0;
    }
  }

  const output = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < width * height; i += 1) {
    const value = result[i];
    const idx = i * 4;
    output[idx] = value;
    output[idx + 1] = value;
    output[idx + 2] = value;
    output[idx + 3] = 255;
  }

  ctx.putImageData(new ImageData(output, width, height), 0, 0);
  return canvas.toDataURL('image/png');
};

const buildProcessedFileName = (originalName: string | undefined, mimeType: string): string => {
  const extension = mimeType === 'image/png' ? '.png' : mimeType === 'image/jpeg' ? '.jpg' : '.png';
  if (!originalName) {
    return `processed-${Date.now()}${extension}`;
  }
  const dotIndex = originalName.lastIndexOf('.');
  const base = dotIndex > 0 ? originalName.slice(0, dotIndex) : originalName;
  return `${base}-canny${extension}`;
};

const dataUrlToFile = async (dataUrl: string, originalName?: string): Promise<File> => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const name = buildProcessedFileName(originalName, blob.type || 'image/png');
  return new File([blob], name, { type: blob.type || 'image/png' });
};

/**
 * FileUpload
 * - 使用项目统一的 Button 组件和 MUI Box 布局，替换原有内联原生 button
 * - 组件只负责文件选择、预览、本地 Object URL 生命周期管理，以及对文件顺序的管理（左移/右移）
 * - 上层表单负责将 UploadFileItem 转换为 base64（gemini）或上传到图床（veo/nanobanana）
 */

export const FileUpload: React.FC<FileUploadProps> = ({
  value,
  onFilesChange,
  accept,
  multiple,
  maxSizeMB = 20,
  maxDimension = DEFAULT_MAX_IMAGE_DIMENSION,
  imageResizeMode = 'fit',
  imageConstraints,
  showHints = true,
  maxFiles,
  onLimitExceeded,
}) => {
  const { t } = useTranslation('components');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const controlled = Array.isArray(value);
  const cloneItems = (list?: UploadFileItem[]): UploadFileItem[] =>
    Array.isArray(list) ? list.map((item) => ({ ...item })) : [];
  const shallowEqualItems = (a: UploadFileItem[], b: UploadFileItem[]): boolean => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      const current = a[i];
      const next = b[i];
      if (current.id !== next.id) return false;
      if (current.src !== next.src) return false;
      if (current.file !== next.file) return false;
    }
    return true;
  };
  const [items, setItems] = useState<UploadFileItem[]>(() => (controlled ? cloneItems(value) : []));
  const itemsRef = useRef<UploadFileItem[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [thresholds, setThresholds] = useState<[number, number]>([50, 150]);
  const [processedSrc, setProcessedSrc] = useState<string>('');
  const [processingImage, setProcessingImage] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);
  const [validationMessage, setValidationMessage] = useState<ValidationMessage | null>(null);
  const editingItem = editingIndex !== null ? items[editingIndex] ?? null : null;

  const normalizedMaxFiles = Number.isFinite(maxFiles) && maxFiles > 0 ? Math.floor(maxFiles) : 1;
  const maxImageDimension =
    Number.isFinite(maxDimension) && maxDimension > 0 ? Math.floor(maxDimension) : DEFAULT_MAX_IMAGE_DIMENSION;
  const allowMultiple = typeof multiple === 'boolean' ? multiple : normalizedMaxFiles > 1;
  const summaryText = showHints
    ? t('fileUpload.summary', {
        count: normalizedMaxFiles,
        reorderHint: allowMultiple ? t('fileUpload.reorderHint') : '',
        acceptHint: accept ? t('fileUpload.acceptHint', { accept }) : '',
      })
    : '';
  const dimensionHint =
    showHints && imageResizeMode === 'fit' && (accept ?? '').toLowerCase().includes('image')
      ? t('fileUpload.dimensionHint', { max: maxImageDimension })
      : '';
  const getPreviewTitle = useCallback(
    (item: UploadFileItem) => item.file?.name ?? item.id ?? t('fileUpload.preview', '预览'),
    [t]
  );
  const {
    previewItem,
    previewTitle,
    previewSrc,
    isVideoPreview,
    openPreview,
    closePreview,
  } = useFilePreview({ getTitle: getPreviewTitle });

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // 仅在“非受控”模式下负责清理本组件内部创建的 Object URL。
  // 受控模式由上层持有数据与生命周期，若此处在卸载时回收 URL，会导致上层再次传回的 src 失效。
  useEffect(() => {
    return () => {
      if (!controlled) {
        releaseAll(itemsRef.current);
      }
    };
  }, [controlled]);

  // 当组件被整体卸载（例如外层弹窗关闭）且存在未应用的处理结果时，自动应用最后一次处理结果
  // 仅在用户尚未点击“取消/应用”且仍处于编辑状态时触发
  useEffect(() => {
    return () => {
      if (editingIndex !== null && processedSrc) {
        void (async () => {
          const list = itemsRef.current;
          const current = list[editingIndex];
          if (!current) return;
          try {
            const file = await dataUrlToFile(processedSrc, current.file?.name);
            const normalized = await normalizeImageFile(file, maxImageDimension);
            const next = list.slice();
            releaseObjectUrl(current);
            next[editingIndex] = {
              ...current,
              file: normalized.file ?? current.file,
              src: normalized.src ?? processedSrc,
              dimensions: normalized.dimensions ?? current.dimensions,
            };
            onFilesChange(next);
          } catch {
            // 静默处理，避免卸载阶段抛错
          }
        })();
      }
    };
  }, [editingIndex, processedSrc, onFilesChange, maxImageDimension]);

  useEffect(() => {
    if (!controlled) {
      return;
    }
    const next = cloneItems(value);
    setItems((prev) => {
      if (shallowEqualItems(prev, next)) {
        return prev;
      }
      return next;
    });
  }, [controlled, value]);

  const applyItemsUpdate = useCallback(
    (updater: (prev: UploadFileItem[]) => UploadFileItem[]) => {
      let nextItems: UploadFileItem[] | null = null;
      let changed = false;
      setItems((prev) => {
        const resolved = updater(prev);
        nextItems = resolved;
        changed = resolved !== prev;
        return resolved;
      });
      if (changed && nextItems) {
        onFilesChange(nextItems);
      }
      return nextItems ?? itemsRef.current;
    },
    [onFilesChange]
  );

  useEffect(() => {
    if (items.length > normalizedMaxFiles) {
      applyItemsUpdate((prev) => {
        if (prev.length <= normalizedMaxFiles) {
          return prev;
        }
        releaseRange(prev, normalizedMaxFiles);
        return prev.slice(0, normalizedMaxFiles);
      });
      onLimitExceeded?.(normalizedMaxFiles);
    }
  }, [applyItemsUpdate, items, normalizedMaxFiles, onLimitExceeded]);

  const handleSelectClick = () => {
    inputRef.current?.click();
  };

  const normalizeMimeType = useCallback((raw: string | undefined): string => {
    return (raw ?? '').split(';')[0]?.trim().toLowerCase() || '';
  }, []);

  const getImageConstraintError = useCallback(
    async (file: File, dimensions?: ImageDimensions): Promise<string | null> => {
      if (!imageConstraints) return null;

      const allowed = imageConstraints.allowedMimeTypes;
      if (Array.isArray(allowed) && allowed.length > 0) {
        const mime = normalizeMimeType(file.type);
        const set = new Set(allowed.map((t) => normalizeMimeType(t)));
        if (!mime || !set.has(mime)) {
          return mime
            ? `图片格式不支持（${mime}），仅支持：${allowed.join('、')}。`
            : '无法识别图片格式，请重新选择 JPG/PNG/BMP/WEBP 图片。';
        }
      }

      if (imageConstraints.disallowPngTransparency && normalizeMimeType(file.type) === 'image/png') {
        const transparency = await inspectPngTransparency(file);
        if (!transparency.ok) {
          return 'PNG 图片解析失败，请重新导出为不透明 PNG 后再上传。';
        }
        if (transparency.hasTransparency) {
          return 'PNG 图片不支持透明通道，请去除透明后再上传。';
        }
      }

      if (dimensions) {
        const minWidth = imageConstraints.minWidth;
        const minHeight = imageConstraints.minHeight;
        const maxWidth = imageConstraints.maxWidth;
        const maxHeight = imageConstraints.maxHeight;

        const tooSmall =
          (typeof minWidth === 'number' && dimensions.width < minWidth) ||
          (typeof minHeight === 'number' && dimensions.height < minHeight);
        const tooLarge =
          (typeof maxWidth === 'number' && dimensions.width > maxWidth) ||
          (typeof maxHeight === 'number' && dimensions.height > maxHeight);

        if (tooSmall || tooLarge) {
          const rangeText =
            typeof minWidth === 'number' &&
            typeof minHeight === 'number' &&
            typeof maxWidth === 'number' &&
            typeof maxHeight === 'number' &&
            minWidth === minHeight &&
            maxWidth === maxHeight
              ? `宽高需在 ${minWidth}~${maxWidth} 像素之间`
              : `尺寸需满足：宽 ${minWidth ?? '-'}~${maxWidth ?? '-'}，高 ${minHeight ?? '-'}~${maxHeight ?? '-'}`;
          return `图片分辨率不符合要求：${rangeText}，当前为 ${dimensions.width}×${dimensions.height}。`;
        }
      }

      return null;
    },
    [imageConstraints, normalizeMimeType]
  );

  /**
   * 处理 FileList：做大小校验、尺寸约束（长边不超过 maxImageDimension）、创建本地 Object URL 并构造 UploadFileItem 列表
   * - 对超规格文件不再插入空占位，直接跳过并给出提示
   */
  const handleFiles = async (list: FileList | File[] | null) => {
    if (!list) return;
    const files = Array.isArray(list) ? list : Array.from(list);
    if (files.length === 0) return;
    let skippedBySize = 0;
    let skippedByError = 0;
    let resizedCount = 0;
    let firstConstraintError: string | null = null;
    const validateImageConstraints = async (file: File, dimensions?: ImageDimensions): Promise<boolean> => {
      const error = await getImageConstraintError(file, dimensions);
      if (!error) return true;
      firstConstraintError = firstConstraintError ?? error;
      return false;
    };

    const processed = await Promise.all(
      files.map(async (f, index) => {
        if (!f) {
          skippedByError += 1;
          return null;
        }
        if (maxSizeMB && f.size > maxSizeMB * 1024 * 1024) {
          skippedBySize += 1;
          return null;
        }
        const isImage = typeof f.type === 'string' ? f.type.startsWith('image/') : false;
        if (!isImage) {
          const src = URL.createObjectURL(f);
          return { id: `${Date.now()}-${index}`, file: f, src };
        }
        try {
          // 不自动缩放：先拿到尺寸并做约束校验，通过后直接使用原文件
          if (imageResizeMode === 'none') {
            const src = URL.createObjectURL(f);
            const dimensions = await readImageDimensions(src);
            if (!dimensions) {
              try {
                URL.revokeObjectURL(src);
              } catch {
                // ignore
              }
              skippedByError += 1;
              return null;
            }

            const ok = await validateImageConstraints(f, dimensions);
            if (!ok) {
              try {
                URL.revokeObjectURL(src);
              } catch {
                // ignore
              }
              skippedByError += 1;
              return null;
            }

            return {
              id: `${Date.now()}-${index}`,
              file: f,
              src,
              dimensions,
            };
          }

          // 默认：自动缩放到 maxImageDimension
          const normalized = await normalizeImageFile(f, maxImageDimension);
          const ok = await validateImageConstraints(normalized.file, normalized.dimensions);
          if (!ok) {
            try {
              URL.revokeObjectURL(normalized.src);
            } catch {
              // ignore
            }
            skippedByError += 1;
            return null;
          }
          if (normalized.resized) resizedCount += 1;

          return {
            id: `${Date.now()}-${index}`,
            file: normalized.file,
            src: normalized.src,
            dimensions: normalized.dimensions,
          };
        } catch {
          skippedByError += 1;
          return null;
        }
      })
    );

    const arr = processed.filter((item): item is ProcessedFileItem => item !== null);

    if (arr.length === 0) {
      if (skippedBySize || skippedByError) {
        const message = firstConstraintError
          ? firstConstraintError
          : skippedBySize > 0
            ? t('fileUpload.validation.sizeExceeded', { count: skippedBySize, maxSizeMB })
            : t('fileUpload.validation.invalidImage', { count: skippedByError });
        setValidationMessage({ type: 'error', text: message });
      }
      return;
    }

    if (!allowMultiple) {
      releaseAll(items);
      if (arr.length > normalizedMaxFiles) {
        releaseRange(arr, normalizedMaxFiles);
        onLimitExceeded?.(normalizedMaxFiles);
      }
      applyItemsUpdate(() => arr.slice(0, normalizedMaxFiles));
      return;
    }

    if (normalizedMaxFiles <= 0) {
      releaseAll(arr);
      return;
    }

    applyItemsUpdate((prev) => {
      const remaining = normalizedMaxFiles - prev.length;
      if (remaining <= 0) {
        releaseAll(arr);
        onLimitExceeded?.(normalizedMaxFiles);
        return prev;
      }
      const toAdd = arr.slice(0, remaining);
      if (toAdd.length < arr.length) {
        releaseRange(arr, remaining);
        onLimitExceeded?.(normalizedMaxFiles);
      }
      return prev.concat(toAdd);
    });

    const noticeParts: string[] = [];
    if (resizedCount) {
      noticeParts.push(t('fileUpload.validation.autoResize', { count: resizedCount, max: maxImageDimension }));
    }
    if (skippedBySize) {
      noticeParts.push(t('fileUpload.validation.sizeExceeded', { count: skippedBySize, maxSizeMB }));
    }
    if (skippedByError) {
      noticeParts.push(t('fileUpload.validation.invalidImage', { count: skippedByError }));
    }
    if (noticeParts.length > 0) {
      setValidationMessage({
        type: skippedBySize || skippedByError ? 'error' : 'info',
        text: noticeParts.join(' '),
      });
    } else {
      setValidationMessage(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    const snapshot = fileList ? Array.from(fileList) : [];
    void handleFiles(snapshot);
    e.currentTarget.value = '';
  };

  /**
   * 删除指定索引文件并释放对应的本地对象 URL（如有）
   */
  const removeAt = (index: number) => {
    applyItemsUpdate((prev) => {
      const it = prev[index];
      releaseObjectUrl(it);
      const next = prev.slice(0, index).concat(prev.slice(index + 1));
      return next;
    });
  };

  /**
   * 移动项：将 fromIndex 项插入到 toIndex 位置（用于排序）
   */
  const move = (fromIndex: number, toIndex: number) => {
    applyItemsUpdate((prev) => {
      if (fromIndex < 0 || fromIndex >= prev.length || toIndex < 0 || toIndex >= prev.length) return prev;
      const next = prev.slice();
      const [it] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, it);
      return next;
    });
  };

  const isImagePreview = useCallback((item: UploadFileItem): boolean => {
    if (!item) return false;
    if (item.file && typeof item.file.type === 'string') {
      return item.file.type.startsWith('image/');
    }
    const src = item.src ?? '';
    if (!src) return false;
    if (src.startsWith('blob:') || src.startsWith('data:image')) {
      return true;
    }
    const questionIndex = src.indexOf('?');
    const sanitized = questionIndex >= 0 ? src.slice(0, questionIndex) : src;
    return /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(sanitized);
  }, []);

  useEffect(() => {
    if (editingIndex === null) {
      return;
    }
    const current = items[editingIndex];
    if (!current || !isImagePreview(current)) {
      setEditingIndex(null);
      setProcessedSrc('');
      setProcessError(null);
      setProcessingImage(false);
    }
  }, [editingIndex, items, isImagePreview]);

  const handleOpenEditor = (index: number) => {
    const target = items[index];
    if (!target || !isImagePreview(target)) {
      return;
    }
    setEditingIndex(index);
    setThresholds([50, 150]);
    setProcessedSrc('');
    setProcessError(null);
    setProcessingImage(false);
  };

  const handleCloseEditor = useCallback(() => {
    setEditingIndex(null);
    setProcessedSrc('');
    setProcessError(null);
    setProcessingImage(false);
  }, []);

  const handleRunProcessing = useCallback(async () => {
    if (editingIndex === null) {
      return;
    }
    const target = items[editingIndex];
    const src = (target?.src ?? '').trim();
    if (!target || !src) {
      setProcessError(t('fileUpload.processMissingSource'));
      return;
    }
    const [rawLow, rawHigh] = thresholds;
    const normalizedLow = clamp(Math.min(rawLow, rawHigh), 0, 254);
    const normalizedHigh = clamp(Math.max(rawLow, rawHigh), normalizedLow + 1, 255);
    if (normalizedLow !== rawLow || normalizedHigh !== rawHigh) {
      setThresholds([normalizedLow, normalizedHigh]);
    }
    setProcessingImage(true);
    setProcessError(null);
    try {
      const result = await applyCannyEdgeDetection(src, normalizedLow, normalizedHigh);
      setProcessedSrc(result);
    } catch {
      setProcessError(t('fileUpload.processError'));
    } finally {
      setProcessingImage(false);
    }
  }, [editingIndex, items, thresholds, t]);

  const handleApplyProcessed = useCallback(async () => {
    if (editingIndex === null || !processedSrc) {
      return;
    }
    const target = items[editingIndex];
    if (!target) {
      setProcessError(t('fileUpload.processApplyError'));
      return;
    }
    try {
      const newFile = await dataUrlToFile(processedSrc, target.file?.name);
      if (imageResizeMode === 'none') {
        const src = URL.createObjectURL(newFile);
        const dimensions = await readImageDimensions(src);
        if (!dimensions) {
          try {
            URL.revokeObjectURL(src);
          } catch {
            // ignore
          }
          setProcessError('无法读取图片尺寸，请更换图片后再试。');
          return;
        }

        const error = await getImageConstraintError(newFile, dimensions);
        if (error) {
          try {
            URL.revokeObjectURL(src);
          } catch {
            // ignore
          }
          setProcessError(error);
          return;
        }

        applyItemsUpdate((prev) => {
          if (editingIndex === null || !prev[editingIndex]) {
            return prev;
          }
          const next = prev.slice();
          const current = next[editingIndex];
          releaseObjectUrl(current);
          next[editingIndex] = {
            ...current,
            file: newFile,
            src,
            dimensions,
          };
          return next;
        });

        setEditingIndex(null);
        setProcessedSrc('');
        setProcessError(null);
        return;
      }

      const normalized = await normalizeImageFile(newFile, maxImageDimension);
      const error = await getImageConstraintError(normalized.file, normalized.dimensions);
      if (error) {
        try {
          URL.revokeObjectURL(normalized.src);
        } catch {
          // ignore
        }
        setProcessError(error);
        return;
      }
      applyItemsUpdate((prev) => {
        if (editingIndex === null || !prev[editingIndex]) {
          return prev;
        }
        const next = prev.slice();
        const current = next[editingIndex];
        releaseObjectUrl(current);
        next[editingIndex] = {
          ...current,
          file: normalized.file,
          src: normalized.src,
          dimensions: normalized.dimensions ?? current?.dimensions,
        };
        return next;
      });
      if (normalized.resized) {
        setValidationMessage({
          type: 'info',
          text: t('fileUpload.validation.autoResize', { count: 1, max: maxImageDimension }),
        });
      }
      setEditingIndex(null);
      setProcessedSrc('');
      setProcessError(null);
    } catch {
      setProcessError(t('fileUpload.processApplyError'));
    }
  }, [applyItemsUpdate, editingIndex, getImageConstraintError, imageResizeMode, items, maxImageDimension, processedSrc, t]);

  useEffect(() => {
    if (!previewItem) {
      return;
    }
    const stillExists = items.some((item) => item === previewItem || (item.id && item.id === previewItem.id));
    if (!stillExists) {
      closePreview();
    }
  }, [closePreview, items, previewItem]);

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={allowMultiple}
        className="hidden"
        onChange={handleInputChange}
      />

      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              onClick={handleSelectClick}
              size="icon-lg"
              aria-label={t('fileUpload.select')}
              className="rounded-xl bg-gradient-to-tr from-indigo-500 to-violet-500 text-white shadow-[0_10px_24px_rgba(99,102,241,0.35)] transition-transform hover:from-indigo-600 hover:to-violet-600 hover:-translate-y-px"
            >
              <ImagePlus className="size-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('fileUpload.select')}</TooltipContent>
        </Tooltip>
        {showHints ? <span className="text-xs text-muted-foreground">{summaryText}</span> : null}
      </div>
      {dimensionHint ? <p className="text-[11px] text-muted-foreground">{dimensionHint}</p> : null}
      {validationMessage ? (
        <p
          className={`text-xs ${validationMessage.type === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}
        >
          {validationMessage.text}
        </p>
      ) : null}

      {items.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {items.map((it, idx) => (
            <div
              key={it.id ?? `${idx}`}
              className="bg-background w-40 rounded border p-2 flex flex-col items-center gap-1"
            >
              {isImagePreview(it) ? (
                // 预览图片
                <img
                  src={it.src}
                  alt={it.file?.name ?? 'file'}
                  className="h-24 w-full rounded object-cover"
                />
              ) : it.src ? (
                <div className="text-center text-xs break-all">{it.src}</div>
              ) : (
                <div className="text-center text-xs break-all">
                  {it.file?.name ?? t('fileUpload.fallbackName')}
                </div>
              )}

              <div className="flex flex-wrap justify-center gap-2">
                {allowMultiple && items.length > 1 && (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span style={tooltipWrapperStyle}>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon-sm"
                            className="transition-transform hover:-translate-y-px"
                            onClick={() => move(idx, idx - 1)}
                            disabled={idx === 0}
                            aria-label={t('fileUpload.moveLeft')}
                          >
                            <ArrowLeft className="size-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{t('fileUpload.moveLeft')}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span style={tooltipWrapperStyle}>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon-sm"
                            className="transition-transform hover:-translate-y-px"
                            onClick={() => move(idx, idx + 1)}
                            disabled={idx === items.length - 1}
                            aria-label={t('fileUpload.moveRight')}
                          >
                            <ArrowRight className="size-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{t('fileUpload.moveRight')}</TooltipContent>
                    </Tooltip>
                  </>
                )}
                {isImagePreview(it) && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span style={tooltipWrapperStyle}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="transition-transform hover:-translate-y-px"
                          onClick={() => handleOpenEditor(idx)}
                          aria-label={t('fileUpload.process')}
                        >
                          <SlidersHorizontal className="size-4" />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{t('fileUpload.process')}</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span style={tooltipWrapperStyle}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="transition-transform hover:-translate-y-px"
                          onClick={() => openPreview(it)}
                          aria-label={t('fileUpload.preview')}
                        >
                          <Eye className="size-4" />
                        </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{t('fileUpload.preview')}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span style={tooltipWrapperStyle}>
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon-sm"
                        className="transition-transform hover:-translate-y-px"
                        onClick={() => removeAt(idx)}
                        aria-label={t('fileUpload.remove')}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{t('fileUpload.remove')}</TooltipContent>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 预览弹窗 */}
      <FilePreviewDialog
        open={Boolean(previewItem)}
        onOpenChange={(open) => {
          if (!open) {
            closePreview();
          }
        }}
        title={previewTitle}
        src={previewSrc}
        isVideo={isVideoPreview}
        emptyText={t('fileUpload.previewUnavailable', '暂无可预览内容')}
        closeText={t('fileUpload.closePreview', '关闭预览')}
      />

      {/* 处理弹窗 */}
      <Dialog open={Boolean(editingItem && editingIndex !== null)} onOpenChange={(open) => !open && handleCloseEditor()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('fileUpload.processTitle')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1 text-center">
                <p className="mb-2 text-sm font-medium">{t('fileUpload.processOriginal')}</p>
                {editingItem?.src ? (
                  <img
                    src={editingItem.src}
                    alt={editingItem.file?.name ?? 'original'}
                    className="mx-auto max-h-80 w-auto rounded object-contain"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">{t('fileUpload.processMissingSource')}</p>
                )}
              </div>
              <div className="flex-1 text-center">
                <p className="mb-2 text-sm font-medium">{t('fileUpload.processResult')}</p>
                {processedSrc ? (
                  <img
                    src={processedSrc}
                    alt={t('fileUpload.processResult')}
                    className="mx-auto max-h-80 w-auto rounded object-contain"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {processingImage ? t('fileUpload.processRunning') : t('fileUpload.processHint')}
                  </p>
                )}
              </div>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium">{t('fileUpload.processThreshold')}</p>
              <Slider
                value={thresholds as unknown as number[]}
                min={0}
                max={255}
                onValueChange={(value) => {
                  if (Array.isArray(value)) {
                    const [low, high] = value as number[];
                    const nextLow = clamp(Math.min(low, high), 0, 254);
                    const nextHigh = clamp(Math.max(low, high), nextLow + 1, 255);
                    setThresholds([nextLow, nextHigh]);
                  }
                }}
              />
              <p className="mt-1 text-sm text-muted-foreground">
                {t('fileUpload.processThresholdValue', { low: thresholds[0], high: thresholds[1] })}
              </p>
              {processError && (
                <p className="mt-2 text-sm text-destructive">{processError}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" onClick={handleCloseEditor}>
              {t('fileUpload.processCancel')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                void handleRunProcessing();
              }}
              disabled={processingImage}
            >
              {processingImage ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  {t('fileUpload.processRun')}
                </>
              ) : (
                t('fileUpload.processRun')
              )}
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={() => {
                void handleApplyProcessed();
              }}
              disabled={!processedSrc || processingImage}
            >
              {t('fileUpload.processApply')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FileUpload;
